import { RouteComponentProps } from "@reach/router";
import {
  BPMTable,
  CheckBox,
  FullPageSpinner,
  Header,
  Button,
  ButtonType,
  FilterBar,
  DownloadDropdown,
  DownloadToggleType,
  ButtonFrameworkVariant,
  Dropdown,
  DropdownToggleType,
} from "../Components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMap } from "../utils/hooks/useData";
import { useCompanyInfo } from "../redux/company";
import { awaitJSON, CopytigerLambdaFetch, CopytigerPyLambdaFetch } from "../utils/fetch-utils";
import { MdOutlineElectricBolt, MdDelete, MdRefresh, MdOutlineAutoAwesome } from "react-icons/md";
import * as R from "ramda";
import { useSetError } from "../redux/modals";

type DescriptionFields = {
  [K in `description${1 | 2 | 3 | 4}`]?: string;
};

type HeadlineFields = {
  [K in `headline${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15}`]?: string;
};

interface TableRow extends DescriptionFields, HeadlineFields {
  id?: string;
  company: string;
  account: string;
  accountName?: string;
  campaignID: string;
  campaignName?: string;
  adGroupId: string;
  adGroupName?: string;
  adGroupCriterionKeywordText: string;
  impressions: number;
  spend: number;
  clicks: number;
  adStrength?: string;
}

interface TrainingData {
  headlines: string[];
  descriptions: string[];
  keywords: string;
}

interface SelectedRowsMap {
  [id: string]: TableRow;
}
interface TrainingProps {
  start: string;
  end: string;
}
const Train = (props: RouteComponentProps & TrainingProps): JSX.Element => {
  const { start, end } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const [hasTrained, setHasTrained] = useState<boolean>(false);

  const [copytigerDataMap, putCopytigerData] = useMap<string, TableRow[]>({});
  const [selectedRows, setSelectedRows] = useState<SelectedRowsMap>({});
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const copytigerDataKey = useMemo(() => `${start}${end}`, [start, end]);
  const [rows, setRows] = useState<TableRow[] | undefined>(undefined);
  const [filterRows, setFilterRows] = useState<{ filter: Function }>({ filter: R.identity });
  const [selectedCampaign, setSelectedCampaign] = useState<string>("all");
  const [selectedAdGroup, setSelectedAdGroup] = useState<string>("all");
  const [language, setLanguage] = useState<string>("english");

  const languageOptions = [
    { value: "english", label: "English" },
    { value: "spanish", label: "Spanish" },
    { value: "french", label: "French" },
    { value: "german", label: "German" },
    { value: "italian", label: "Italian" },
    { value: "portuguese", label: "Portuguese" },
  ];
  const campaignOptions = useMemo(() => {
    if (!rows) {
      return [{ value: "all", label: "All Campaigns" }];
    }

    const uniqueCampaigns = new Set();
    const campaignMap = new Map();

    rows.forEach(row => {
      const campaignKey = row.campaignID;
      if (!uniqueCampaigns.has(campaignKey)) {
        uniqueCampaigns.add(campaignKey);
        const displayName = row.campaignName || row.campaignID;
        campaignMap.set(campaignKey, `${displayName} (${row.campaignID})`);
      }
    });

    const options = [{ value: "all", label: "All Campaigns" }];
    Array.from(campaignMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .forEach(([key, label]) => {
        options.push({ value: key, label });
      });

    return options;
  }, [rows]);

  const adGroupOptions = useMemo(() => {
    if (!rows) {
      return [{ value: "all", label: "All Ad Groups" }];
    }

    const uniqueAdGroups = new Set();
    const adGroupMap = new Map();

    rows.forEach(row => {
      const adGroupKey = row.adGroupId;
      if (!uniqueAdGroups.has(adGroupKey)) {
        uniqueAdGroups.add(adGroupKey);
        const displayName = row.adGroupName || row.adGroupId;
        adGroupMap.set(adGroupKey, `${displayName} (${row.adGroupId})`);
      }
    });

    const options = [{ value: "all", label: "All Ad Groups" }];
    Array.from(adGroupMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .forEach(([key, label]) => {
        options.push({ value: key, label });
      });

    return options;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const filteredRows: TableRow[] = [];
    for (const row of rows || []) {
      if (row.impressions !== 0 && filterRows.filter(row)) {
        if (selectedCampaign === "all" || row.campaignID === selectedCampaign) {
          if (selectedAdGroup === "all" || row.adGroupId === selectedAdGroup) {
            filteredRows.push(row);
          }
        }
      }
    }
    return filteredRows;
  }, [rows, filterRows, selectedCampaign, selectedAdGroup]);

  const companyInfo = useCompanyInfo();
  const company = companyInfo.cid;
  const setError = useSetError();

  const handleTrainCopytiger = useCallback(async () => {
    const selectedRowsArray = Object.values(selectedRows);

    if (selectedRowsArray.length === 0) {
      setError({ message: "Please select at least one row to train with" });
      return;
    }

    setIsTraining(true);

    // transform data to match dataframe shape
    const transformedTrainingData: TrainingData[] = selectedRowsArray.map(row => ({
      headlines: [
        row.headline1,
        row.headline2,
        row.headline3,
        row.headline4,
        row.headline5,
        row.headline6,
        row.headline7,
        row.headline8,
        row.headline9,
        row.headline10,
        row.headline11,
        row.headline12,
        row.headline13,
        row.headline14,
        row.headline15,
      ].filter((h): h is string => Boolean(h)),
      descriptions: [
        row.description1,
        row.description2,
        row.description3,
        row.description4,
      ].filter((d): d is string => Boolean(d)),
      keywords: row.adGroupCriterionKeywordText,
    }));

    try {
      const response = await CopytigerPyLambdaFetch("/trainCopytigerModel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sheetURL: "https://docs.google.com/spreadsheets/d/PLACEHOLDERFORNOW",
          companyID: company,
          trainingData: transformedTrainingData,
          language: language,
        }),
      });
      if (response.ok) {
        setSelectedRows({});
        setSelectAll(false);
        setHasTrained(true);
      } else {
        const errorText = await response.text();
        setError({ message: `Training failed. Details: ${errorText}` });
      }
    } catch (error) {
      setError({ message: "Training request failed. Please check your network connection." });
    } finally {
      setIsTraining(false);
    }
  }, [selectedRows, company, setError, language]);
  const trainLabel = isTraining
    ? hasTrained
      ? "Retraining…"
      : "Training…"
    : hasTrained
    ? "Retrain Model"
    : "Train Model";
  const trainIcon = isTraining ? (
    <MdOutlineElectricBolt />
  ) : hasTrained ? (
    <MdRefresh />
  ) : (
    <MdOutlineAutoAwesome />
  );

  useEffect(() => {
    (async () => {
      if (copytigerDataMap[copytigerDataKey]) {
        setRows(copytigerDataMap[copytigerDataKey]);
        return;
      }
      setIsLoading(true);
      try {
        const resp = await CopytigerLambdaFetch("/getCopytigerTrainingOptions", {
          method: "GET",
          params: {
            company: company,
            startDate: start,
            endDate: end,
          },
        });
        const raw: any[] = await awaitJSON(resp);
        const data: TableRow[] = raw.map((obj, index) => {
          return {
            id: obj.id,
            company: obj.company,
            account: obj.account,
            accountName: obj.accountname || "",
            campaignID: obj.campaignid,
            campaignName: obj.campaignname || "",
            adGroupId: obj.adgroupid,
            adGroupName: obj.adgroupname || "",
            adGroupCriterionKeywordText: obj.adgroupcriterionkeywordtext,
            impressions: Number(obj.impressions),
            spend: Number(obj.spend),
            clicks: Number(obj.clicks),
          };
        });
        setRows(data);
        putCopytigerData(copytigerDataKey, data);
      } catch (e) {
        console.error("Copytiger fetch failed:", e);
        setRows([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [start, end, copytigerDataKey, putCopytigerData, copytigerDataMap, company]);

  const exportFilteredData = () => {};
  if (rows && selectedRows && filteredRows) {
  }

  const checkboxRenderer = useCallback(
    (row: TableRow) => {
      const idString = row.id!.toString();
      return (
        <CheckBox
          checked={selectedRows.hasOwnProperty(idString)}
          onCheck={() => {
            if (selectedRows[idString]) {
              setSelectedRows(current => {
                const newState = { ...current };
                delete newState[idString];
                return newState;
              });
            } else {
              setSelectedRows(current => ({
                ...current,
                [idString]: row,
              }));
            }
          }}
        />
      );
    },
    [selectedRows]
  );

  const allTableHeaders: Header[] = useMemo(() => {
    const baseHeaders: Header[] = [
      {
        label: "",
        name: "id",
        width: 90,
        nonInteractive: true,
        renderer: checkboxRenderer,
      },
      {
        label: "Account ID",
        name: "account",
        flex: 1,
        contentGetter: (row: TableRow) => row.account,
      },
      {
        label: "Account Name",
        name: "accountName",
        flex: 1,
        contentGetter: (row: TableRow) => row.accountName || "",
      },
      {
        label: "Campaign ID",
        name: "campaignID",
        flex: 1,
        contentGetter: (row: TableRow) => row.campaignID,
      },
      {
        label: "Campaign Name",
        name: "campaignName",
        flex: 1,
        contentGetter: (row: TableRow) => row.campaignName || "",
      },
      {
        label: "Ad Group ID",
        name: "adGroupId",
        flex: 1,
        contentGetter: (row: TableRow) => row.adGroupId,
      },
      {
        label: "Ad Group Name",
        name: "adGroupName",
        flex: 1,
        contentGetter: (row: TableRow) => row.adGroupName || "",
      },
      {
        label: "Keyword Text",
        name: "adGroupCriterionKeywordText",
        flex: 1,
        contentGetter: (row: TableRow) => row.adGroupCriterionKeywordText,
      },
      {
        label: "Impressions",
        name: "impressions",
        flex: 1,
        contentGetter: (row: TableRow) => row.impressions,
      },
      {
        label: "Spend",
        name: "spend",
        flex: 1,
        contentGetter: (row: TableRow) => `$${Math.round(row.spend).toLocaleString("en-US")}`,
      },
      {
        label: "Clicks",
        name: "clicks",
        flex: 1,
        contentGetter: (row: TableRow) => row.clicks,
      },
      {
        label: "Ad Strength",
        name: "adStrength",
        flex: 1,
        contentGetter: (row: TableRow) => row.adStrength || "",
      },
    ];

    const descriptionHeaders: Header[] = [];
    for (let i = 1; i <= 4; i++) {
      descriptionHeaders.push({
        label: `Description ${i}`,
        name: `description${i}`,
        flex: 1,
        contentGetter: (row: TableRow) => row[`description${i}` as keyof TableRow] || "",
      });
    }

    const headlineHeaders: Header[] = [];
    for (let i = 1; i <= 15; i++) {
      headlineHeaders.push({
        label: `Headline ${i}`,
        name: `headline${i}`,
        flex: 1,
        contentGetter: (row: TableRow) => row[`headline${i}` as keyof TableRow] || "",
      });
    }

    return [...baseHeaders, ...descriptionHeaders, ...headlineHeaders];
  }, [checkboxRenderer]);

  const tableHeaders: Header[] = allTableHeaders;

  const headersRenderer = useCallback(
    ({ data, columnIndex }) => {
      if (columnIndex === 0) {
        return (
          <div className="headerCheckboxContainer">
            <span>Include</span>
            <label className="headerCheckboxLabel">
              <CheckBox
                checked={selectAll}
                onCheck={() => {
                  if (selectAll) {
                    setSelectedRows({});
                  } else {
                    const all: Record<string, TableRow> = {};
                    for (const row of filteredRows) {
                      all[row.id!.toString()] = row;
                    }
                    setSelectedRows(all);
                  }
                  setSelectAll(!selectAll);
                }}
              />
            </label>
          </div>
        );
      }

      return data;
    },
    [selectAll, filteredRows]
  );

  const generateDefaultTotals = useCallback(() => {
    const defaultTotals: any = {
      spend: 0,
      impressions: 0,
      clicks: 0,
      adStrength: "",
    };

    for (let i = 1; i <= 4; i++) {
      defaultTotals[`description${i}`] = "";
    }

    for (let i = 1; i <= 15; i++) {
      defaultTotals[`headline${i}`] = "";
    }

    return defaultTotals;
  }, []);

  const totals = useMemo(() => {
    const selectedRowsArray = Object.values(selectedRows);

    if (selectedRowsArray.length === 0) {
      return generateDefaultTotals();
    }

    const totalSpend = selectedRowsArray.reduce((sum, row) => sum + Math.round(row.spend), 0);
    const totalImpressions = selectedRowsArray.reduce((sum, row) => sum + row.impressions, 0);
    const totalClicks = selectedRowsArray.reduce((sum, row) => sum + row.clicks, 0);

    return {
      ...generateDefaultTotals(),
      spend: totalSpend,
      impressions: totalImpressions,
      clicks: totalClicks,
    };
  }, [selectedRows, generateDefaultTotals]);

  const totalsRenderer = useCallback(
    ({ data, style, classes, columnIndex }) => {
      const header = tableHeaders[columnIndex];
      let formattedData = data;

      if (header.name === "spend" && typeof data === "number") {
        formattedData = `$${Math.round(data).toLocaleString("en-US")}`;
      } else if (
        (header.name === "impressions" || header.name === "clicks") &&
        typeof data === "number"
      ) {
        formattedData = data.toLocaleString("en-US");
      }

      return (
        <div style={style} className={classes.join(" ")}>
          {formattedData}
        </div>
      );
    },
    [tableHeaders]
  );

  return (
    <div className="bpm-copytiger">
      <div className="copytigerContainer">
        <div className="copytigerTopSection">
          Select Ads to Include in Copytiger Model Training
          {isLoading ? (
            <FullPageSpinner />
          ) : rows !== undefined && rows.length > 0 ? (
            <div className="copytigerTableContainer fillParent">
              <div className="TableControlsContainer">
                <FilterBar
                  hasAdvanced={true}
                  onFilter={filter => {
                    setFilterRows({ filter });
                  }}
                  options={rows && rows.length > 0 ? R.keys(rows[0]) : []}
                  size="lg"
                  lines={R.defaultTo([], rows)}
                />
                <div className="filtersContainer">
                  <div className="campaignFilterContainer">
                    <Dropdown
                      type={DropdownToggleType.OUTLINED}
                      design="secondary"
                      value={selectedCampaign}
                      options={campaignOptions}
                      onChange={setSelectedCampaign}
                      placeholder="Filter by Campaign"
                    />
                  </div>
                  <div className="adGroupFilterContainer">
                    <Dropdown
                      type={DropdownToggleType.OUTLINED}
                      design="secondary"
                      value={selectedAdGroup}
                      options={adGroupOptions}
                      onChange={setSelectedAdGroup}
                      placeholder="Filter by Ad Group"
                    />
                  </div>
                </div>
                <DownloadDropdown
                  size="sm"
                  type={DownloadToggleType.FILLED}
                  design="primary"
                  onClickOptions={[exportFilteredData]}
                  menuOptions={["XLSX"]}
                  disabledMenuOptions={{
                    XLSX: !rows || rows.length === 0,
                    PNG: true,
                  }}
                />
              </div>
              <BPMTable
                data={filteredRows}
                headers={tableHeaders}
                headersRenderer={headersRenderer}
                filterBar={false}
                totals={totals}
                totalsRenderer={totalsRenderer}
              />
              <div className="copytigerTableFooter">
                <div className="copytigerBottomSection copytigerBottomSectionInline">
                  <div className="selectedRowsInlineBar">
                    <div className="selectedRowsDisplayInline">
                      Selected Training Data ({R.keys(selectedRows).length} rows)
                    </div>
                    <div className="selectedRowsButtonWrapperInline">
                      <div className="languageDropdownContainer">
                        <label className="languageDropdownLabel">Language of Training Data:</label>
                        <Dropdown
                          type={DropdownToggleType.OUTLINED}
                          design="secondary"
                          value={language}
                          options={languageOptions}
                          onChange={setLanguage}
                          placeholder="Select Language of Training Data"
                        />
                      </div>
                      <Button
                        className="discardCopytigerButton"
                        type={ButtonType.OUTLINED}
                        design="secondary"
                        size="lg"
                        variant={ButtonFrameworkVariant.LEADING_ICON}
                        icon={<MdDelete />}
                        onClick={() => {
                          setSelectedRows({});
                          setSelectAll(false);
                        }}
                      >
                        Discard Edits
                      </Button>
                      <Button
                        className="trainCopytigerButton"
                        type={ButtonType.FILLED}
                        design="primary"
                        size="lg"
                        variant={ButtonFrameworkVariant.LEADING_ICON}
                        icon={trainIcon}
                        onClick={handleTrainCopytiger}
                        disabled={isTraining}
                      >
                        {trainLabel}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="copytigerNoData">
              No data for {start} - {end}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Train;
